/**
 * Auditoría (auditoria.html): el registro inmutable — cada respuesta con su
 * traza, cada toque de memoria, cada resolución de cola y cada evento de
 * sistema, con actor y referencia enlazada. Cierra el argumento «no es
 * magia»: lo que no se puede explicar aquí, no se despliega. Solo añade —
 * nunca reescribe.
 * DATOS: ACTOS es SEMILLA fiel del canon F6 (auditoria.seed.tsx, los 38
 * actos del prototipo) — el registro real llega con su propio endpoint
 * (F5c). El día en números (sidepane) se deriva del registro, no se
 * mantiene aparte. Estados del contrato + ?estado=, como el resto del
 * Taller. La retícula propia (.auditoria-grid) es local a esta página: el
 * registro no es una cola HITL y no usa la columna compartida de /cola.
 */
import { useLayoutEffect, useRef, useState } from "react";
import { useSearch } from "@tanstack/react-router";
import {
  Badge, BarRow, Banner, Button, Card, Empty, Fainted, Mono, PageBtn, PageHead, Pagination, SearchInput,
  Select, Sidepane, Skeleton, StatCell, StatGrid, Table, Th, Toolbar, useSlashFocus, useToast,
} from "@skein/ui";
import { fechaTabla } from "../implantaciones/fecha";
import { ACTOS, botonRef, refNode, type Acto, type ActoTipo, type ActorTipo } from "./auditoria.seed";
import { filtrarActos, statsDia } from "./filtro";
import { useAsideSlot } from "../../shell/asideSlot";

const POR_PAG = 12;
const TIPOS: ReadonlyArray<{ value: ActoTipo | ""; label: string }> = [
  { value: "", label: "Todos los actos" },
  { value: "respuesta", label: "respuestas del asesor" },
  { value: "memoria", label: "memoria (sí / retiro / apunte)" },
  { value: "curacion", label: "curación (drafts / publicar)" },
  { value: "cola", label: "resoluciones de cola" },
  { value: "sistema", label: "sistema (sync / conflictos)" },
];
const ACTORES: ReadonlyArray<{ value: ActorTipo | ""; label: string }> = [
  { value: "", label: "Todos los actores" },
  { value: "asesor", label: "asesor (IA)" },
  { value: "executive", label: "executives" },
  { value: "equipo", label: "equipo Skein" },
  { value: "sistema", label: "sistema" },
];

/** «hoy HH:MM» reutilizando el idioma de fechas F6 (fecha.ts) — el acto es de hoy, siempre. */
function isoHoy(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date();
  d.setHours(h ?? 0, m ?? 0, 0, 0);
  return d.toISOString();
}
function TipoBadge({ t }: { t: ActoTipo }) {
  if (t === "memoria") return <Badge tone="ok">memoria</Badge>;
  if (t === "curacion") return <Badge tone="accent">curación</Badge>;
  if (t === "cola") return <Badge tone="info">cola</Badge>;
  if (t === "sistema") return <Badge>sistema</Badge>;
  return <Badge>respuesta</Badge>;
}
function Fila({ a, interactive }: { a: Acto; interactive: boolean }) {
  return (
    <tr>
      <td><Mono><Fainted>{fechaTabla(isoHoy(a.hora), false)}</Fainted></Mono></td>
      <td><TipoBadge t={a.tipo} /></td>
      <td>{a.acto}</td>
      <td><Mono><Fainted style={{ fontSize: "var(--sk-fs-2xs)" }}>{a.actor}</Fainted></Mono></td>
      <td>{refNode(a.donde, interactive)}</td>
      <td>{a.ref ? botonRef(a.ref, interactive) : null}</td>
    </tr>
  );
}
const HEAD = <tr><Th>Momento</Th><Th>Tipo</Th><Th>Acto</Th><Th>Actor</Th><Th>Dónde</Th><Th aria-label="Referencia" /></tr>;
function SkeletonRows() {
  const c = (w: string) => <Skeleton style={{ width: w }} />;
  return (
    <tbody>
      {Array.from({ length: 8 }, (_, i) => (
        <tr key={i}>
          <td>{c("4rem")}</td><td>{c("4.5rem")}</td><td><Skeleton style={{ width: "16rem", maxWidth: "100%" }} /></td><td>{c("3.5rem")}</td><td>{c("7rem")}</td><td />
        </tr>
      ))}
    </tbody>
  );
}

export function AuditoriaPage() {
  const say = useToast();
  const search = useSearch({ strict: false }) as { estado?: string };
  const inputRef = useSlashFocus();
  const stats = statsDia(ACTOS);
  const maxBar = Math.max(stats.respuestas, stats.memoria, stats.cola, stats.sistema, stats.curacion, 1);
  const ancho = (n: number) => `${((n / maxBar) * 4.5).toFixed(2)}rem`;
  // el aside vive en la columna del shell (ownAside en nav.ts), como en /cola
  useAsideSlot(
    <Sidepane className="aside" aria-label="Hoy, en números" title="Hoy, en números">
      <Card flush>
        <StatGrid>
          <StatCell n={stats.total} label="actos" />
          <StatCell n={stats.respuestas} label="respuestas con traza" />
        </StatGrid>
        <div style={{ paddingBottom: "var(--sk-sp-2)" }}>
          <BarRow k="respuestas" width={ancho(stats.respuestas)} v={stats.respuestas} />
          <BarRow k="memoria" width={ancho(stats.memoria)} v={stats.memoria} />
          <BarRow k="cola" width={ancho(stats.cola)} v={stats.cola} />
          <BarRow k="sistema" width={ancho(stats.sistema)} v={stats.sistema} />
          <BarRow k="curación" width={ancho(stats.curacion)} v={stats.curacion} />
        </div>
      </Card>
      <Empty solid inline title="Toda respuesta es explicable.">
        {" "}Cada una guarda su traza: skill, criterios recibidos, decisiones, seguimientos y datos con hora de sincronización.
      </Empty>
    </Sidepane>,
  );
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState<ActoTipo | "">("");
  const [actor, setActor] = useState<ActorTipo | "">("");
  const [pag, setPag] = useState(1);
  const wrapRef = useRef<HTMLDivElement>(null);

  const visibles = filtrarActos(ACTOS, { q, tipo, actor });
  const pags = Math.max(1, Math.ceil(visibles.length / POR_PAG));
  const pagReal = Math.min(pag, pags);
  const enPagina = visibles.slice((pagReal - 1) * POR_PAG, (pagReal - 1) * POR_PAG + POR_PAG);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (el && !el.style.minHeight && enPagina.length === POR_PAG) el.style.minHeight = `${el.offsetHeight}px`;
  }, [enPagina.length]);


  const forced = search.estado;
  const view = forced === "vacio" || forced === "cargando" || forced === "error" ? forced : "lista";
  const stale = ACTOS.slice(0, 6);

  return (
    <>
      <PageHead
        title="Auditoría"
        meta={<>
          <Badge>{stats.total} actos · hoy</Badge>
          <Badge tone="ok">registro íntegro</Badge>
          <Fainted style={{ fontSize: "var(--sk-fs-2xs)", alignSelf: "center" }}>solo añade — nunca reescribe · lo que no se puede explicar aquí, no se despliega</Fainted>
        </>}
        actions={<Button onClick={() => say("Exportaría el registro filtrado (CSV firmado).")}>Exportar…</Button>}
      />
      <div className="listzone">
          {view === "lista" && (
            <Toolbar>
              <SearchInput inputRef={inputRef} style={{ flex: 1, minWidth: "14rem" }} placeholder="Buscar por acto, actor o referencia…" aria-label="Buscar en el registro" shortcut="/" value={q} onChange={(e) => { setQ(e.target.value); setPag(1); }} />
              <Select ariaLabel="Filtrar por tipo de acto" style={{ width: "13rem" }} value={tipo} onChange={(v) => { setTipo(v); setPag(1); }} options={TIPOS} />
              <Select ariaLabel="Filtrar por actor" style={{ width: "12rem" }} value={actor} onChange={(v) => { setActor(v); setPag(1); }} options={ACTORES} />
            </Toolbar>
          )}

          {view === "vacio" && (
            <Empty glyph="✳" title="El registro está vacío">
              Cada respuesta del asesor, cada publicación y cada toque de memoria dejará aquí su rastro con quién, cuándo y su traza.
              El primer acto lo escribirá el primer mensaje de la primera casa.
            </Empty>
          )}

          {view === "cargando" && (
            <Table dense busy label="Cargando el registro"><thead>{HEAD}</thead><SkeletonRows /></Table>
          )}

          {view === "error" && (
            <>
              <Banner tone="danger" style={{ marginBottom: "var(--sk-gap)" }}>
                <b>No llego al registro.</b> Lo que ves puede estar incompleto — el registro real es inmutable y no ha perdido nada. Reintento solo cada 30 s.{" "}
                <Button variant="link" onClick={() => say("Reintentando la conexión…")}>reintentar ahora</Button>
              </Banner>
              <Table dense stale label="Registro (copia desactualizada)">
                <thead>{HEAD}</thead>
                <tbody>{stale.map((a, i) => <Fila key={i} a={a} interactive={false} />)}</tbody>
              </Table>
            </>
          )}

          {view === "lista" && (
            <section>
              <Table dense wrapRef={wrapRef} label="Registro del día">
                <thead>{HEAD}</thead>
                <tbody>
                  {enPagina.map((a, i) => <Fila key={i} a={a} interactive />)}
                  {enPagina.length === 0 && (
                    <tr><td colSpan={6}><Empty inline title="Nada casa con ese filtro." style={{ margin: "var(--sk-sp-3)" }}>Prueba a quitar el acto o el actor.</Empty></td></tr>
                  )}
                </tbody>
              </Table>
              <div className="list-foot" style={{ marginTop: "var(--sk-sp-4)" }}>
                <Pagination meta={visibles.length ? `${(pagReal - 1) * POR_PAG + 1}–${Math.min(pagReal * POR_PAG, visibles.length)} de ${visibles.length} · hoy` : "0 actos con ese filtro"}>
                  <PageBtn disabled={pagReal <= 1} onClick={() => setPag(pagReal - 1)} aria-label="Página anterior">‹</PageBtn>
                  {Array.from({ length: pags }, (_, i) => <PageBtn key={i} current={i + 1 === pagReal} onClick={() => setPag(i + 1)}>{i + 1}</PageBtn>)}
                  <PageBtn disabled={pagReal >= pags} onClick={() => setPag(pagReal + 1)} aria-label="Página siguiente">›</PageBtn>
                </Pagination>
              </div>
            </section>
          )}
      </div>
    </>
  );
}
